import {clearBearerToken} from './token'
import {bounceToPwa} from './runtime-api'

/** What the dashboard should do about a VN-mode HTTP/transport failure. */
export type VnRecoveryAction = 'clear-and-bounce' | 'bounce' | 'ignore'

/**
 * Classify a VN-mode network failure into a single recovery action. This is the ONE source of
 * truth both HTTP choke points (GraphQL `onError`, REST `backendRequest`) consume, so their
 * behaviour can never drift apart.
 *
 * - `401` → the bearer is revoked/expired: clear the token, then bounce to the PWA to re-auth.
 * - `undefined` → a response-LESS transport failure (fetch rejected, no HTTP status → the host is
 *   unreachable / rotated away): bounce so the PWA re-discovers a live host and returns to `/app`.
 * - anything else (5xx, other 4xx) → a reachable host returning an error: surface it, do NOT
 *   bounce. Bouncing here would re-discover the SAME live host and spin an infinite /app⇄/ loop.
 */
export const classifyNetworkFailure = (statusCode: number | undefined): VnRecoveryAction => {
  if (statusCode === 401) return 'clear-and-bounce'
  if (statusCode === undefined) return 'bounce'
  return 'ignore'
}

/**
 * Execute a recovery action. `clear-and-bounce` AWAITS the token clear before bouncing, so the
 * revoked bearer is durably gone from IndexedDB before the PWA re-authenticates — otherwise the
 * un-awaited delete can abort on navigation and the stale token survives into a 401 loop.
 */
export const recoverFromVnFailure = async (
  action: VnRecoveryAction,
  deps: {clear?: () => Promise<void>; bounce?: () => void} = {},
): Promise<void> => {
  const {clear = clearBearerToken, bounce = bounceToPwa} = deps
  if (action === 'ignore') return
  if (action === 'clear-and-bounce') await clear()
  bounce()
}
